import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ChevronRightIcon,
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ClockIcon,
  PaperAirplaneIcon,
  ChatBubbleLeftRightIcon,
  QuestionMarkCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { contactApi } from '../services/api';

const ContactPage: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
    category: 'general',
    honeypot: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const contactInfo = [
    {
      icon: <PhoneIcon className="h-6 w-6" />,
      title: "تلفن",
      value: "+1 (647) 309-9929",
      description: "در ساعات کاری در دسترس"
    },
    {
      icon: <EnvelopeIcon className="h-6 w-6" />,
      title: "ایمیل پشتیبانی",
      value: "support@timcheh.ca",
      description: "پاسخ در عرض ۲۴ ساعت"
    },
    {
      icon: <MapPinIcon className="h-6 w-6" />,
      title: "آدرس دفتر",
      value: "تورنتو، کانادا",
      description: "دفتر مرکزی"
    },
    {
      icon: <ClockIcon className="h-6 w-6" />,
      title: "ساعات کاری",
      value: "۹ صبح تا ۶ عصر",
    }
  ];

  const categories = [
    { value: 'general', label: 'درخواست عمومی' },
    { value: 'technical', label: 'مشکل فنی' },
    { value: 'account', label: 'مشکلات حساب کاربری' },
    { value: 'billing', label: 'صورتحساب' },
    { value: 'report', label: 'گزارش مشکل' },
    { value: 'suggestion', label: 'پیشنهاد' }
  ];

  const faqs = [
    {
      question: "چگونه آگهی ثبت کنم؟",
      answer: "برای ثبت آگهی، ابتدا به حساب کاربری خود وارد شوید، سپس روی 'ثبت آگهی جدید' کلیک کنید و فرم را تکمیل کنید."
    },
    {
      question: "ثبت آگهی رایگان است؟",
      answer: "بله، می‌توانید از پکیج‌های رایگان برای ثبت آگهی در تیمچه استفاده کنید و همچنین از پکیج‌های تبلیغاتی برای افزایش دید آگهی خود استفاده کنید."
    },
    {
      question: "چگونه می‌توانم آگهی خود را ویرایش کنم؟",
      answer: "به داشبورد خود وارد شوید، به 'آگهی‌های من' بروید، آگهی مورد نظر را پیدا کنید و ویرایش را کلیک کنید."
    },
    {
      question: "چرا آگهی من تأیید نشده است؟",
      answer: "آگهی‌ها باید با قوانین سایت مطابقت داشته باشند. مطمئن شوید که عکس‌ها واضح، توضیحات کامل و قیمت معقول است."
    }
  ];

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const response = await contactApi.submit({
        name: formData.name,
        email: formData.email,
        category: formData.category,
        subject: formData.subject,
        message: formData.message,
        honeypot: formData.honeypot
      });

      toast.success(response.data.message || 'پیام شما با موفقیت ارسال شد! به زودی با شما تماس خواهیم گرفت.', {
        duration: 5000,
        position: 'top-center'
      });

      setFormData({
        name: '',
        email: '',
        subject: '',
        message: '',
        category: 'general',
        honeypot: ''
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'ارسال پیام ناموفق بود. لطفاً بعداً دوباره تلاش کنید.';
      toast.error(errorMessage, {
        duration: 5000,
        position: 'top-center'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex" aria-label="Breadcrumb">
            <ol className="flex items-center space-x-2">
              <li>
                <Link to="/" className="text-gray-500 hover:text-gray-700 transition-colors">
                    خانه
                </Link>
              </li>
              <li>
                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
              </li>
              <li>
                <span className="text-gray-900 font-medium">تماس با ما</span>
              </li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-primary-50 via-white to-blue-50 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-72 h-72 bg-gradient-to-r from-primary-200 to-blue-200 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-gradient-to-r from-purple-200 to-pink-200 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center bg-gradient-to-r from-primary-500 to-blue-500 px-6 py-3 rounded-full mb-8 shadow-lg">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-white mr-2" />
            <span className="text-white font-semibold text-sm">ما اینجا هستیم</span>
          </div>
          
          <h1 className="text-6xl font-semibold text-gray-900 mb-8 leading-tight">
            تماس 
            <span className="bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text text-transparent">با ما</span>
          </h1>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-12">
            سوال، پیشنهاد یا مشکلی دارید؟ تیم پشتیبانی ما آماده کمک است.
            از طریق فرم زیر یا با استفاده از اطلاعات تماس با ما ارتباط برقرار کنید.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-primary-50 to-blue-50 rounded-2xl p-8 group-hover:from-primary-100 group-hover:to-blue-100 transition-all duration-300 transform group-hover:-translate-y-2 shadow-lg hover:shadow-xl">
                  <div className="w-16 h-16 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:from-primary-200 group-hover:to-primary-300 transition-all duration-300 transform group-hover:scale-110">
                    <div className="text-primary-600">
                      {info.icon}
                    </div>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{info.title}</h3>
                  <p className="text-primary-600 font-medium mb-2">{info.value}</p>
                  <p className="text-gray-600 text-sm">{info.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & FAQ */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-8 shadow-xl">
              <div className="text-center mb-8">
                    <div className="inline-flex items-center bg-gradient-to-r from-green-100 to-blue-100 px-4 py-2 rounded-full mb-4">
                      <PaperAirplaneIcon className="h-5 w-5 text-green-600 mr-2" />
                      <span className="text-green-700 font-semibold text-sm">ارسال پیام</span>
                    </div>
                    <h2 className="text-3xl font-semibold text-gray-900 mb-4">فرم تماس</h2>
                    <p className="text-gray-600">پیام خود را برای ما ارسال کنید</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      نام و نام خانوادگی *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                      placeholder="نام خود را وارد کنید"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                      placeholder="ایمیل خود را وارد کنید"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    دسته‌بندی
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                  >
                    {categories.map((category) => (
                      <option key={category.value} value={category.value}>
                        {category.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    موضوع *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                      placeholder="موضوع را وارد کنید"
                  />
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    پیام *
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none"
                    placeholder="پیام خود را اینجا بنویسید..."
                  />
                </div>

                {/* Honeypot field - hidden from users, bots will fill it */}
                <div style={{ position: 'absolute', left: '-9999px', opacity: 0, pointerEvents: 'none' }}>
                  <input
                    type="text"
                    name="honeypot"
                    value={formData.honeypot}
                    onChange={handleInputChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white py-4 px-6 rounded-xl font-semibold hover:from-primary-700 hover:to-primary-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                      {isSubmitting ? (
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-top-transparent mr-2"></div>
                      در حال ارسال...
                    </div>
                  ) : (
                    <div className="flex items-center justify-center">
                      <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                      ارسال پیام
                    </div>
                  )}
                </button>
              </form>
            </div>

            {/* FAQ Section */}
            <div>
              <div className="text-center mb-8">
                <div className="inline-flex items-center bg-gradient-to-r from-yellow-100 to-orange-100 px-4 py-2 rounded-full mb-4">
                  <QuestionMarkCircleIcon className="h-5 w-5 text-yellow-600 mr-2" />
                  <span className="text-yellow-700 font-semibold text-sm">سوالات متداول</span>
                </div>
                <h2 className="text-3xl font-semibold text-gray-900 mb-4">پاسخ به سوالات شما</h2>
                <p className="text-gray-600">ممکن است پاسخ خود را اینجا پیدا کنید</p>
              </div>

              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-start">
                      <span className="w-6 h-6 bg-primary-100 text-primary-600 rounded-full flex items-center justify-center text-sm font-semibold mr-3 mt-0.5 flex-shrink-0">
                        {index + 1}
                      </span>
                      {faq.question}
                    </h3>
                    <p className="text-gray-600 leading-relaxed ml-9">
                      {faq.answer}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-gradient-to-r from-blue-50 to-primary-50 rounded-2xl p-6 border border-blue-100">
                <div className="flex items-start">
                  <ExclamationTriangleIcon className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">نیاز به کمک بیشتری دارید؟</h3>
                    <p className="text-gray-600 mb-4">
                      اگر پاسخ خود را پیدا نکردید، می‌توانید مستقیماً با تیم پشتیبانی ما تماس بگیرید.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3">
                      <a
                        href="tel:+16471234567"
                        className="inline-flex items-center justify-center bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
                      >
                        <PhoneIcon className="h-4 w-4 mr-2" />
                        تماس با ما
                      </a>
                      <a
                        href="mailto:support@timcheh.ca"
                        className="inline-flex items-center justify-center bg-white border border-primary-200 text-primary-600 px-4 py-2 rounded-lg font-medium hover:bg-primary-50 transition-colors"
                      >
                        <EnvelopeIcon className="h-4 w-4 mr-2" />
                        ارسال ایمیل
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-blue-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-48 h-48 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-64 h-64 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-semibold text-white mb-6">هنوز سوالی دارید؟</h2>
          <p className="text-xl text-primary-100 mb-8 leading-relaxed">
            تیم پشتیبانی ما آماده پاسخگویی به تمام سوالات شماست
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="mailto:support@timcheh.ca"
              className="bg-white text-primary-600 px-8 py-4 rounded-xl font-semibold hover:bg-gray-50 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
            >
              ارسال ایمیل
            </a>
            <Link
              to="/help"
              className="bg-primary-500 text-white px-8 py-4 rounded-xl font-semibold hover:bg-primary-400 transition-all duration-300 border-2 border-white/20"
            >
              مرکز راهنمایی
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage; 